import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import BasePage from "../components/BasePage";
import LaunchStrategy from "./roadMapDoc/TokenLaunchStrategy";
import UcWavesRoadmap from "./roadMapDoc/UcWavesRoadmap";
import InfrastructureAndEcosystemPractices from "./roadMapDoc/InfrastructureAndEcosystemPractices";
import Marketing from "./roadMapDoc/MarketingEventsAndSponsorshipStrategy";
import { motion } from "framer-motion";
import { ArrowUp } from "lucide-react";

import Hero from "../components/Hero";
import imgR from "../assets/img/img-hero-r.webp";
import imgL from "../assets/img/img-hero-l.webp";

const sections = [
  {
    id: "launch-strategy",
    label: "Launch Strategy",
    component: <LaunchStrategy />,
  },
  {
    id: "ucwaves-roadmap",
    label: "UcWaves Roadmap",
    component: <UcWavesRoadmap />,
  },
  {
    id: "infrastructure-ecosystem",
    label: "Infrastructure & Ecosystem",
    component: <InfrastructureAndEcosystemPractices />,
  },
  { id: "marketing", label: "Marketing", component: <Marketing /> },
];

type RoadmapPageProps = Record<string, unknown>;

const RoadmapPage: React.FC<RoadmapPageProps> = () => {
  const [showNav, setShowNav] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(sections[0].id);
  const [showTopBtn, setShowTopBtn] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowNav(scrollY > 200);
      setShowTopBtn(scrollY > 200);

      const offset = 100;
      let current = sections[0].id;
      sections.forEach((sec) => {
        const el = document.getElementById(sec.id);
        if (el && el.offsetTop <= scrollY + offset) {
          current = sec.id;
        }
      });
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <BasePage
      title="UcWaves Project Roadmap & Strategy"
      description="Explore UcWaves' roadmap, launch approach, and wallet safety standards across its decentralized ecosystem."
      keywords="UcWaves, roadmap, token launch, wallet security, milesNAUNes"
    >
      <div className="relative">
        <Hero />
      </div>

      {showNav && (
        <nav className="fixed inset-x-0 top-20 z-50 w-full bg-white shadow-md py-4 px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm font-semibold">
            {sections.map((sec) => (
              <a
                key={sec.id}
                href={`#${sec.id}`}
                onClick={() => setActiveSection(sec.id)}
                className={`transition px-2 py-1 rounded ${
                  activeSection === sec.id
                    ? "text-[var(--color-secondary-dark)] bg-[var(--color-background)]"
                    : "text-[var(--color-primary-dark)] hover:text-[var(--color-secondary-dark)]"
                }`}
              >
                {sec.label}
              </a>
            ))}
          </div>
        </nav>
      )}

      <main>
        <section className="relative py-10 px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto text-justify">
          <img
            src={imgL}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute left-4 md:left-16 xl:left-32  top-5 transform -translate-y-5  w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />
          <img
            src={imgR}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute right-4 md:right-16 xl:right-32 top-5 transform -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />

          <div className="relative z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              viewport={{ once: true }}
              className="flex items-center justify-center"
            >
              <h1 className="text-6xl sm:text-6xl md:text-[64px] font-bold  md:mt-15 text-secondary-light text-center shadow-red-700 mb-32">
                UnCensored Waves <br />
                Roadmap Overview
              </h1>
            </motion.div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto space-y-20">
          {sections.map((sec) => (
            <section key={sec.id} id={sec.id} className="scroll-mt-24">
              {sec.component}
            </section>
          ))}
        </div>
      </main>

      {showTopBtn && (
        <motion.button
          onClick={scrollToTop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-6 right-6 p-3 rounded-full bg-[var(--color-primary-dark)] text-white shadow-lg hover:bg-[var(--color-secondary-dark)] focus:outline-none"
        >
          <ArrowUp size={24} />
        </motion.button>
      )}
    </BasePage>
  );
};

export default RoadmapPage;
